import React, { useCallback, useContext, useMemo, useState } from "react";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import { RoleContext } from "../../Contexts/RoleContext";
import { useAppModule } from "../../Contexts/AppContext";
import { getVisibleAppIds } from "../../navigation/appMenus";
import { APP_SWITCHER_ORDER, getAppLauncherMeta, getAppLabel } from "../../navigation/appMeta";
import {
  getPinnedAppIds,
  getRecentAppIds,
  togglePinnedApp,
} from "../../navigation/appLauncherPreferences";
import { isCustomAppId, toClientAppId, toDbAppId } from "../../navigation/customAppIds";
import { getNavIconComponent } from "../../navigation/muiNavIcons";
import { swatchForAppKey } from "../../navigation/dynamicAppSwatch";
import styles from "./AppLauncher.module.scss";

function sortVisibleApps(visibleSet, pinnedIds, orderedLauncherIds) {
  const pinned = pinnedIds.filter((id) => visibleSet.has(id));
  const rest = orderedLauncherIds.filter((id) => visibleSet.has(id) && !pinned.includes(id));
  return [...pinned, ...rest];
}

export default function AppLauncher({ open, anchorEl, onClose }) {
  const { role } = useContext(RoleContext);
  const { currentApp, setCurrentApp, apps: navApps, resolveAppLabel } = useAppModule();
  const [pinVersion, setPinVersion] = useState(0);
  const [search, setSearch] = useState("");

  const visibleStatic = useMemo(() => getVisibleAppIds(role), [role]);
  const visibleSet = useMemo(() => {
    const s = new Set(visibleStatic);
    (navApps || []).forEach((a) => {
      if (!a.is_system) s.add(toClientAppId(a.id));
    });
    return s;
  }, [visibleStatic, navApps]);

  const orderedLauncherIds = useMemo(() => {
    const systemKeys = APP_SWITCHER_ORDER.filter((id) => visibleStatic.includes(id));
    const custom = (navApps || [])
      .filter((a) => !a.is_system && !a.hidden)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((a) => toClientAppId(a.id));
    return [...systemKeys, ...custom];
  }, [visibleStatic, navApps]);

  const pinnedIds = useMemo(() => getPinnedAppIds(), [pinVersion, open]);

  const orderedKeys = useMemo(
    () => sortVisibleApps(visibleSet, pinnedIds, orderedLauncherIds),
    [visibleSet, pinnedIds, orderedLauncherIds]
  );

  const recentIds = useMemo(
    () => getRecentAppIds().filter((id) => visibleSet.has(id) && id !== currentApp),
    [visibleSet, currentApp, pinVersion, open]
  );

  const resolveMeta = useCallback(
    (appId) => {
      if (!isCustomAppId(appId)) {
        return getAppLauncherMeta(appId);
      }
      const raw = toDbAppId(appId);
      const rec = (navApps || []).find((a) => a.id === raw);
      if (!rec) {
        return getAppLauncherMeta("leads");
      }
      const Icon = getNavIconComponent(rec.icon);
      const swatch = swatchForAppKey(rec.key);
      return { label: rec.name, Icon, swatch };
    },
    [navApps]
  );

  const resolveLabel = useCallback(
    (appId) => (isCustomAppId(appId) ? resolveAppLabel(appId) : getAppLabel(appId)),
    [resolveAppLabel]
  );

  const filteredKeys = useMemo(() => {
    if (!search.trim()) return orderedKeys;
    const q = search.toLowerCase().trim();
    return orderedKeys.filter((id) => resolveLabel(id).toLowerCase().includes(q));
  }, [orderedKeys, search, resolveLabel]);

  const handlePick = useCallback(
    (key) => {
      setCurrentApp(key);
      setSearch("");
      onClose();
    },
    [setCurrentApp, onClose]
  );

  const handlePin = useCallback((e, appId) => {
    e.stopPropagation();
    e.preventDefault();
    togglePinnedApp(appId);
    setPinVersion((v) => v + 1);
  }, []);

  const handleClose = useCallback(() => {
    setSearch("");
    onClose();
  }, [onClose]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          className: styles.panel,
          elevation: 0,
        },
      }}
      transitionDuration={{ enter: 220, exit: 160 }}
    >
      <Box className={styles.panelInner}>
        {/* header */}
        <Box className={styles.panelHeader}>
          <Typography variant="subtitle2" className={styles.panelTitle}>
            Apps
          </Typography>
          <span className={styles.appCount}>
            {filteredKeys.length} available
          </span>
        </Box>

        {/* search */}
        <div className={styles.searchWrap}>
          <SearchRoundedIcon className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search apps"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        {/* recents */}
        {!search && recentIds.length > 0 && (
          <Box className={styles.recentRow}>
            <Typography variant="caption" className={styles.sectionLabel}>
              Recent
            </Typography>
            <Box className={styles.recentChips}>
              {recentIds.slice(0, 5).map((id) => {
                const { Icon, swatch } = resolveMeta(id);
                return (
                  <button
                    key={id}
                    type="button"
                    className={styles.recentChip}
                    onClick={() => handlePick(id)}
                  >
                    <span
                      className={styles.recentIconWell}
                      style={{
                        background: swatch.bg,
                        color: swatch.fg,
                        "--well-ring": swatch.ring,
                      }}
                    >
                      <Icon className={styles.recentIcon} />
                    </span>
                    <span>{resolveLabel(id)}</span>
                  </button>
                );
              })}
            </Box>
          </Box>
        )}

        {/* section label for all apps */}
        {!search && (
          <Typography variant="caption" className={styles.sectionLabel}>
            All Apps
          </Typography>
        )}
        {search && filteredKeys.length > 0 && (
          <Typography variant="caption" className={styles.sectionLabel}>
            Results
          </Typography>
        )}

        {/* grid */}
        {filteredKeys.length > 0 ? (
          <Box className={styles.grid}>
            {filteredKeys.map((appId, i) => {
              const { Icon, swatch } = resolveMeta(appId);
              const selected = appId === currentApp;
              const pinned = pinnedIds.includes(appId);
              const label = resolveLabel(appId);
              return (
                <button
                  key={appId}
                  type="button"
                  className={`${styles.tile} ${selected ? styles.tileActive : ""}`}
                  style={{ "--tile-i": i }}
                  onClick={() => handlePick(appId)}
                >
                  <span className={`${styles.pinSlot} ${pinned ? styles.pinSlotVisible : ""}`}>
                    <IconButton
                      size="small"
                      className={styles.pinBtn}
                      aria-label={pinned ? "Unpin app" : "Pin app"}
                      onClick={(e) => handlePin(e, appId)}
                    >
                      {pinned ? (
                        <StarIcon style={{ fontSize: 16, color: "#FFD700" }} />
                      ) : (
                        <StarBorderIcon style={{ fontSize: 14, color: "rgba(100,116,139,0.55)" }} />
                      )}
                    </IconButton>
                  </span>
                  <span
                    className={`${styles.iconWell} ${selected ? styles.iconWellActive : ""}`}
                    style={{
                      background: swatch.bg,
                      color: swatch.fg,
                      "--well-ring": swatch.ring,
                    }}
                  >
                    <Icon className={styles.tileIcon} />
                  </span>
                  <span className={styles.tileLabel}>{label}</span>
                </button>
              );
            })}
          </Box>
        ) : (
          <Box className={styles.emptyState}>
            <SearchOffRoundedIcon className={styles.emptyIcon} />
            <span className={styles.emptyText}>No apps match "{search}"</span>
          </Box>
        )}
      </Box>
    </Popover>
  );
}
